import "maplibre-gl/dist/maplibre-gl.css";
import maplibregl from "maplibre-gl";
import { useEffect, useRef } from "react";
import { DEFAULT_CENTER, DEFAULT_ZOOM, MAPCN_STYLE_URL } from "@/lib/maps/mapcn";
import type { DeliveryMarker } from "@/features/delivery/utils/tracking-map-state";
import {
  createDeliveryMarkerElement,
  createDeliveryMarkerPopupContent,
  type DeliveryMarkerPopupVariant,
  type DeliveryTrackingMarkerLabels,
  type DeliveryTrackingPopupLabels,
} from "@/features/delivery/utils/tracking-marker-element";

type Props = {
  markers: DeliveryMarker[];
  /** Accessible labels for each marker kind/freshness. */
  markerLabels: DeliveryTrackingMarkerLabels;
  /** Short copy rendered inside the on-click popup. */
  popupLabels: DeliveryTrackingPopupLabels;
  /**
   * Centered copy rendered as a banner above the map when the helper returns
   * a notice code. When null, no banner is shown.
   */
  noticeText?: string | null;
  /**
   * Centered empty-state copy rendered as an overlay when the helper reports
   * an empty map. When null, no overlay is shown.
   */
  emptyStateText?: string | null;
  className?: string;
  innerClassName?: string;
};

// =============================================================================
// Delivery tracking map (Issues #79, #80, #81, #82)
//
// Receives an ordered marker array plus translated labels. The marker kinds
// already encode origin/destination/Rider semantics and freshness encodes
// whether the Rider is live or stale, so this component is a thin wrapper
// around MapLibre:
//
//   * Each marker is built from `createDeliveryMarkerElement`, which returns
//     a custom DOM element with `role="img"` and a kind-specific SVG glyph.
//   * Stale Rider markers carry the `delivery-marker--stale` class so the
//     coordinate is preserved while the visual treatment changes.
//   * Each marker has a popup attached via `setPopup`, which opens on click /
//     tap only — never by default.
//   * Markers are keyed by `kind-freshness` so a stale-to-live transition
//     tears down the stale marker and mounts a live one in place.
//
// `noticeText` renders a centered banner above the map for non-happy paths
// (preparing, waiting for Rider, missing destination, etc.). `emptyStateText`
// renders a centered overlay with a slightly stronger surface treatment when
// the helper reports an empty marker list, so the map never silently shows
// default geography.
//
// All business rules (privacy, freshness, notice selection, final-state
// action) live in `buildDeliveryTrackingMapState`; this component stays
// MapLibre-only.
// =============================================================================

function toLngLat(marker: DeliveryMarker): [number, number] {
  return [marker.lng, marker.lat];
}

function markerKey(marker: DeliveryMarker): string {
  return `${marker.kind}-${marker.kind === "rider" ? (marker.freshness ?? "live") : "static"}`;
}

function popupVariantFor(marker: DeliveryMarker): DeliveryMarkerPopupVariant {
  if (marker.kind === "origin") return "origin";
  if (marker.kind === "destination") return "destination";
  return marker.freshness === "stale" ? "lastKnown" : "riderLive";
}

function accessibleLabelFor(marker: DeliveryMarker, labels: DeliveryTrackingMarkerLabels): string {
  if (marker.kind === "origin") return labels.origin;
  if (marker.kind === "destination") return labels.destination;
  return marker.freshness === "stale" ? labels.riderStale : labels.riderLive;
}

export function DeliveryTrackingMap({
  markers,
  markerLabels,
  popupLabels,
  noticeText,
  emptyStateText,
  className,
  innerClassName = "h-72 w-full overflow-hidden rounded-md border",
}: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const markerRefs = useRef<Map<string, maplibregl.Marker>>(new Map());

  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    const center =
      markers.length > 0
        ? toLngLat(markers[0])
        : ([DEFAULT_CENTER.lng, DEFAULT_CENTER.lat] as [number, number]);

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: MAPCN_STYLE_URL,
      center,
      zoom: DEFAULT_ZOOM,
    });
    mapRef.current = map;

    return () => {
      markerRefs.current.forEach((marker) => marker.remove());
      markerRefs.current.clear();
      map.remove();
      mapRef.current = null;
    };
    // Map setup is one-shot; marker updates flow through the effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) {
      return;
    }

    const nextKeys = new Set(markers.map(markerKey));

    for (const marker of markers) {
      const key = markerKey(marker);
      const lngLat = toLngLat(marker);
      const existing = markerRefs.current.get(key);

      if (existing) {
        existing.setLngLat(lngLat);
        continue;
      }

      const element = createDeliveryMarkerElement({
        kind: marker.kind,
        freshness: marker.kind === "rider" ? marker.freshness : undefined,
        accessibleLabel: accessibleLabelFor(marker, markerLabels),
      });

      const popupCopy =
        marker.kind === "origin"
          ? popupLabels.origin
          : marker.kind === "destination"
            ? popupLabels.destination
            : marker.freshness === "stale"
              ? popupLabels.lastKnown
              : popupLabels.riderLive;

      const popup = new maplibregl.Popup({
        offset: 18,
        closeButton: false,
        closeOnClick: true,
      }).setDOMContent(
        createDeliveryMarkerPopupContent({
          variant: popupVariantFor(marker),
          copy: popupCopy,
        }),
      );

      const created = new maplibregl.Marker({ element })
        .setLngLat(lngLat)
        .setPopup(popup)
        .addTo(map);
      markerRefs.current.set(key, created);
    }

    for (const [key, marker] of markerRefs.current) {
      if (!nextKeys.has(key)) {
        marker.remove();
        markerRefs.current.delete(key);
      }
    }

    if (markers.length > 1) {
      const bounds = new maplibregl.LngLatBounds();
      for (const marker of markers) {
        bounds.extend(toLngLat(marker));
      }
      map.fitBounds(bounds, { padding: 60, duration: 600, maxZoom: 16 });
    } else if (markers.length === 1) {
      map.easeTo({ center: toLngLat(markers[0]), duration: 600, zoom: DEFAULT_ZOOM });
    }
  }, [markers, markerLabels, popupLabels]);

  return (
    <div className={className}>
      <div className="relative h-full w-full">
        <div ref={containerRef} className={innerClassName} />
        {emptyStateText ? (
          <div
            data-testid="delivery-tracking-empty-state"
            className="pointer-events-none absolute inset-0 flex items-center justify-center bg-background/70 p-4 backdrop-blur-sm"
          >
            <p className="rounded-md border bg-background px-4 py-2 text-sm font-medium text-foreground shadow-sm">
              {emptyStateText}
            </p>
          </div>
        ) : null}
        {noticeText && !emptyStateText ? (
          <div className="pointer-events-none absolute left-1/2 top-3 z-10 -translate-x-1/2">
            <p
              data-testid="delivery-tracking-notice"
              className="max-w-md rounded-full border bg-background/90 px-3 py-1 text-center text-xs font-medium text-foreground shadow-sm backdrop-blur"
            >
              {noticeText}
            </p>
          </div>
        ) : null}
      </div>
    </div>
  );
}
